import { FC, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, Views, Event, EventProps } from 'react-big-calendar';
import cx from 'clsx';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { enUS } from 'date-fns/locale';
import { format, parse, startOfWeek, getDay } from 'date-fns';

import { Campaign } from 'src/api/campaigns/types';
import { CampaignEvent } from './use-campaign-calendar';
import { AddCampaignSlotModal } from './AddSlotModal/add-campaign-slot-modal';
import { EditSlotModal } from './EditSlotModal';
import { CustomToolbar } from './Toolbar';
import { CampaignCalendarHeader } from './Header';
import { CustomEvent } from './Event';
import { MonthCellWrapper } from './MonthCellWrapper';
import { MonthDateHeader } from './MonthDateHeader';
import { TimeSlotWrapper } from './TimeSlotWrapper';
import { useCampaignCalendar } from './use-campaign-calendar';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import styles from './styles.module.css';

interface CustomComponentProps {
  children: React.ReactNode;
  value: Date;
}

interface DateHeaderProps {
  date: Date;
  label: string;
}

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const DnDCalendar = withDragAndDrop(Calendar);

interface CampaignCalendarProps {
  data: Campaign;
}

export const CampaignCalendar: FC<CampaignCalendarProps> = ({ data }) => {
  const {
    selectedSlot,
    isAddModalOpen,
    currentDate,
    currentView,
    events,
    campaignStartDate,
    campaignEndDate,
    handleNavigation,
    handleViewChange,
    handleAddEvent,
    handleSelectSlot,
    handleSelectEvent,
    handleSelecting,
    handleEventDrop,
    handleEventResize,
    handleDateClick,
    closeModal,
  } = useCampaignCalendar(data);

  const dayPropGetter = useCallback(
    (date: Date) => {
      const campaignStart = new Date(data.date_from);
      const campaignEnd = new Date(data.date_to);
      const isInRange = date >= campaignStart && date <= campaignEnd;

      return {
        className: cx({
          [styles.day]: true,
          [styles.disabledDay]: !isInRange,
        }),
      };
    },
    [data.date_from, data.date_to]
  );

  const eventPropGetter = useCallback(
    (event: Event) => {
      const campaignEvent = event as CampaignEvent;
      const eventIndex = data.slots.findIndex(
        (slot) => slot.id === campaignEvent.slot.id
      );
      const isEven = eventIndex % 2 === 0;

      return {
        className: isEven ? styles.eventEven : styles.eventOdd,
      };
    },
    [data.slots]
  );

  const customComponents = {
    toolbar: CustomToolbar,
    event: (props: EventProps<CampaignEvent>) => (
      <CustomEvent {...props} currentView={currentView} />
    ),
    timeSlotWrapper: ({ children, value }: CustomComponentProps) => (
      <TimeSlotWrapper
        value={value}
        campaignStartDate={campaignStartDate}
        campaignEndDate={campaignEndDate}
        onAddEvent={handleAddEvent}
      >
        {children}
      </TimeSlotWrapper>
    ),
    dateCellWrapper: ({ children, value }: CustomComponentProps) => {
      const isInCampaignRange =
        value >= campaignStartDate && value <= campaignEndDate;

      return (
        <MonthCellWrapper
          value={value}
          isInCampaignRange={isInCampaignRange}
          currentView={currentView}
          onAddEvent={handleAddEvent}
        >
          {children}
        </MonthCellWrapper>
      );
    },
    month: {
      dateHeader: ({ date, label }: DateHeaderProps) => {
        const isInCampaignRange =
          date >= campaignStartDate && date <= campaignEndDate;

        return (
          <MonthDateHeader
            date={date}
            label={label}
            isInCampaignRange={isInCampaignRange}
            onDateClick={handleDateClick}
          />
        );
      },
    },
  } as any;

  return (
    <div className={styles.container}>
      <CampaignCalendarHeader
        campaignName={data.name}
        dateFrom={data.date_from}
        dateTo={data.date_to}
        campaignType={data.campaign_type}
        campaignGoal={data.campaign_goal}
        locationType={data.location_type}
      />

      <DnDCalendar
        localizer={localizer}
        events={events}
        date={currentDate}
        view={currentView}
        startAccessor={(event: Event) => event.start || new Date()}
        endAccessor={(event: Event) => event.end || new Date()}
        selectable
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        onSelectSlot={handleSelectSlot}
        onSelecting={handleSelecting}
        onEventResize={handleEventResize}
        resizable
        defaultView={Views.MONTH}
        views={[Views.MONTH, Views.WEEK, Views.DAY]}
        step={30}
        timeslots={2}
        className={styles.calendar}
        onNavigate={handleNavigation}
        onView={handleViewChange}
        dayPropGetter={dayPropGetter}
        eventPropGetter={eventPropGetter}
        components={customComponents}
      />

      {isAddModalOpen && selectedSlot && (
        <AddCampaignSlotModal
          isOpen={isAddModalOpen}
          onClose={closeModal}
          slotInfo={selectedSlot}
          campaignId={data.id}
          minDate={campaignStartDate}
          maxDate={campaignEndDate}
        />
      )}

      <EditSlotModal />
    </div>
  );
};
